import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Download } from "lucide-react";
import api from "@/lib/api";
import StatusBadge from "@/components/StatusBadge";
import { fmtDate } from "@/lib/utils";
import SearchableSelect from "@/components/SearchableSelect";

// The API's card_or_ap value stays "AP" (totals are keyed on it); spelled out for people.
const payLabel = (v) => (v === "AP" ? "Accounts Payable" : v);
const fmt = (n) => (n == null ? "—" : `$${Number(n).toLocaleString(undefined, { minimumFractionDigits: 2 })}`);
const firstOfMonth = () => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10); };
const today = () => new Date().toISOString().slice(0, 10);

// Meeting 3 (Package 47) — one page per purchaser: all their POs and
// Expense Log entries, card and AP, by day and job.
export default function PurchaserReport() {
  const [team, setTeam] = useState([]);
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState(firstOfMonth());
  const [dateTo, setDateTo] = useState(today());
  const [report, setReport] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/contractor/team").then((r) => {
      const members = r.data.filter((m) => m.status !== "pending_invite");
      setTeam(members);
      if (members.length) setUserId((u) => u || members[0].id);
    });
  }, []);

  useEffect(() => {
    if (!userId) return;
    api.get("/contractor/reports/purchaser", { params: { user_id: userId, date_from: dateFrom || undefined, date_to: dateTo || undefined } })
      .then((r) => setReport(r.data));
  }, [userId, dateFrom, dateTo]);

  // Group rows: day → job.
  const days = [];
  (report?.rows || []).forEach((r) => {
    let day = days.find((d) => d.date === r.date);
    if (!day) { day = { date: r.date, jobs: [], total: 0 }; days.push(day); }
    let job = day.jobs.find((j) => j.key === (r.job || "—"));
    if (!job) { job = { key: r.job || "—", rows: [], total: 0 }; day.jobs.push(job); }
    job.rows.push(r);
    job.total += r.amount;
    day.total += r.amount;
  });

  const exportCsv = () => {
    const header = ["Date", "Job", "Number", "Type", "Vendor", "Vendor invoice #", "Card / Accounts Payable", "Quoted", "Actual", "Status", "Approved by", "Memo"];
    const cell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [header.map(cell).join(",")].concat(report.rows.map((r) => [
      r.date, r.job, r.number, r.kind === "po" ? "PO" : "Expense", r.vendor, r.vendor_invoice_number, payLabel(r.card_or_ap),
      r.quoted ?? "", r.amount, r.status, r.approved_by, r.memo,
    ].map(cell).join(",")));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = `purchaser-report-${(report.purchaser.name || "").replace(/\s+/g, "-").toLowerCase()}-${dateFrom || "all"}-${dateTo || "all"}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const open = (r) => navigate(r.kind === "po" ? `/contractor/purchase-orders/${r.id}` : `/contractor/expense-log/${r.id}`);

  return (
    <div className="p-6 md:p-8" data-testid="purchaser-report-page">
      <h1 className="text-2xl font-extrabold tracking-tight">Purchaser Report</h1>
      <p className="text-slate-500 text-sm mt-1">Everything one person bought — POs and Expense Log, card and accounts payable — by day and job.</p>

      <div className="mt-5 flex flex-wrap items-end gap-3">
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">Purchaser</label>
          <SearchableSelect value={userId} onChange={(e) => setUserId(e.target.value)} data-testid="report-purchaser-select" className="mt-1 block h-10 px-3 rounded-lg border border-slate-200 text-sm min-w-[200px]">
            {team.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </SearchableSelect>
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">From</label>
          <input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="mt-1 block h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wide text-slate-400 font-bold">To</label>
          <input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="mt-1 block h-10 px-3 rounded-lg border border-slate-200 text-sm" />
        </div>
        <button onClick={exportCsv} disabled={!report || report.rows.length === 0} data-testid="report-export-csv" className="flex items-center gap-2 h-10 px-4 rounded-lg border border-slate-200 text-[13px] font-semibold hover:bg-slate-50 disabled:opacity-50">
          <Download size={14} /> Export CSV
        </button>
      </div>

      {report && (
        <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="report-totals">
          {[["Total", report.totals.all], ["Card", report.totals.card], ["Accounts Payable", report.totals.ap], ["Cash / Check", (report.totals.cash || 0) + (report.totals.check || 0)]].map(([label, v]) => (
            <div key={label} className="border border-slate-200 rounded-xl p-3.5">
              <div className="text-[11px] uppercase tracking-wide text-slate-400 font-semibold">{label}</div>
              <div className="mono font-bold text-[17px] mt-0.5">{fmt(v)}</div>
            </div>
          ))}
        </div>
      )}

      <div className="mt-6 space-y-6">
        {report && days.length === 0 && <div className="py-10 text-center text-slate-400 text-sm">Nothing in this date range.</div>}
        {days.map((day) => (
          <div key={day.date} data-testid={`report-day-${day.date}`}>
            <div className="flex items-baseline justify-between border-b border-slate-200 pb-1.5">
              <h2 className="font-bold text-[14px]">{fmtDate(day.date)}</h2>
              <span className="mono font-bold text-[13px]">{fmt(day.total)}</span>
            </div>
            {day.jobs.map((job) => (
              <div key={job.key} className="mt-2">
                <div className="flex items-baseline justify-between text-[12.5px] text-slate-500 font-semibold">
                  <span>{job.key}</span><span className="mono">{fmt(job.total)}</span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-[13px] mt-1">
                    <tbody>
                      {job.rows.map((r) => (
                        <tr key={`${r.kind}-${r.id}`} onClick={() => open(r)} className="cursor-pointer hover:bg-slate-50 border-b border-slate-50">
                          <td className="py-2 mono text-sky-600 font-medium whitespace-nowrap">{r.number}</td>
                          <td className="py-2 text-slate-500">{r.kind === "po" ? "PO" : "Expense"}</td>
                          <td className="py-2">{r.vendor || "—"}</td>
                          <td className="py-2 text-slate-500">{payLabel(r.card_or_ap)}</td>
                          <td className="py-2 text-right mono text-slate-400">{r.quoted != null ? fmt(r.quoted) : ""}</td>
                          <td className="py-2 text-right mono font-medium">{fmt(r.amount)}</td>
                          <td className="py-2 pl-3"><StatusBadge status={r.status} /></td>
                          <td className="py-2 pl-3 text-[12px] text-slate-500">{r.approved_by || ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
